import { ChevronRight, Phone } from "@mui/icons-material";
import { Button, ButtonProps, LinkTypeMap, Link as MuiLink, Stack, Typography, TypographyProps, useTheme } from "@mui/material";
import { useSeekaConverge } from "@seeka-labs/converge-react";
import React, { ElementType, ReactNode, useMemo } from "react";
import { getStrapiContentItemIsHidden, StrapiContentStatus } from "../../shared/utils";

export type LinkPropsBase = {
    url: string | undefined | null
    label?: string | undefined | null
    links?: LinkPropsBase[] | null | undefined
    hidden?: boolean
    buttonType?: undefined | 'Button filled' | 'Button outlined' | 'Text' | null
    buttonColor?: 'Primary' | 'Secondary' | 'Default' | 'White' | 'Brand' | 'PrimaryGradient' | null | undefined
    icon?: undefined | 'Right arrow' | 'Phone' | null
    requiredPublishType?: StrapiContentStatus
    mobileLabel?: string | undefined | null
    description?: string | undefined | null
    relativeLinkComponent?: ElementType;
    forceLink?: boolean;
    LinkProps?: LinkTypeMap['props']
    ButtonProps?: ButtonProps
}

const getMuiButtonVariant = (buttonType: LinkPropsBase['buttonType'], def: ButtonProps['variant']) => {
    switch (buttonType) {
        case 'Button filled': return 'contained'
        case 'Button outlined': return 'outlined'
        case 'Text': return 'text'
        default: return def
    }
}

const getMuiButtonColor = (buttonColor: LinkPropsBase['buttonColor'], def: ButtonProps['color']) => {
    switch (buttonColor) {
        case 'Primary': return 'primary'
        case 'Secondary': return 'secondary'
        case 'Default': return 'inherit'
        case 'White': return 'inherit'
        case 'Brand': return 'primary'
        default: return def
    }
}

export type LinkProps = Omit<ButtonProps, 'href'> & LinkPropsBase & {
    labelToIconMapping?: Record<string, ReactNode>
}

export function LinkLabelAndDescription({ label, description, onClick, labelToIconMapping, TypographyProps }: { label: string | undefined | null, description: string | undefined | null, onClick?: () => void, labelToIconMapping?: Record<string, ReactNode>, TypographyProps?: TypographyProps }) {
    const theme = useTheme();

    // const labelText = useMemo

    const labelEl = useMemo(() => {
        return label && labelToIconMapping && labelToIconMapping[label] ? labelToIconMapping[label] : label
    }, [label, labelToIconMapping])

    if (!description) {
        if (!onClick) {
            return labelEl;
        }
        else {
            return <Stack gap={2} direction="row" onClick={onClick} alignItems="center" justifyContent="space-between">
                <Typography variant="h4" sx={{ fontSize: theme.typography.h5.fontSize }} gutterBottom={false} {...TypographyProps}>{labelEl}</Typography>
                <ChevronRight />
            </Stack>
        }
    }

    return (<Stack gap={2} direction="row" onClick={onClick} alignItems="center" justifyContent="space-between">
        <Stack gap={0.5}>
            <Typography variant="h4" sx={{ fontSize: theme.typography.h5.fontSize }} gutterBottom={false} {...TypographyProps}>{labelEl}</Typography>
            <Typography variant="body1">{description}</Typography>
        </Stack>
        <ChevronRight />
    </Stack>)
}

export const onLinkClick = (url: string | undefined | null, e: React.MouseEvent | undefined, onClick: ((e: React.MouseEvent) => void) | undefined | null) => {
    if (onClick) {
        onClick(e as any)
    }
}

export function Link({ url, label, description, relativeLinkComponent, forceLink, LinkProps, children, labelToIconMapping, endIcon, ButtonProps, icon, requiredPublishType, buttonType, buttonColor, hidden, ...other }: LinkProps) {

    const isTel = url && url.startsWith("tel:")
    const isAbsoluteUrl = url && (url.startsWith("http") || isTel)
    const theme = useTheme();
    const seeka = useSeekaConverge();

    // console.log('heree relative', label, relativeLinkComponent)

    if (getStrapiContentItemIsHidden(requiredPublishType, hidden)) return null;

    const onClick = (e: React.MouseEvent) => {
        onLinkClick(url || '', e, other.onClick as any)
        if (isTel && seeka) {
            seeka.track.custom('PhoneNumberClick')
        }
    }

    // console.log('heree link', forceLink, label, url)

    if (forceLink) {
        return relativeLinkComponent && !isAbsoluteUrl ?
            <MuiLink underline='none' component={relativeLinkComponent} color="inherit" href={url || undefined} to={url || undefined} {...other as any} onClick={onClick} sx={{ fontSize: 'inherit', ...other.sx, ...LinkProps?.sx }} {...LinkProps}>
                {children ? children : <LinkLabelAndDescription labelToIconMapping={labelToIconMapping} label={label} description={description} />}
            </MuiLink>
            :
            <MuiLink underline='none' color="inherit" href={url || undefined} {...isAbsoluteUrl ? { target: '_blank', rel: 'noopener noreferrer' } : undefined} to={url || undefined} {...other as any} onClick={onClick} sx={{ fontSize: 'inherit', ...other.sx, ...LinkProps?.sx }} {...LinkProps}>
                {children ? children : <LinkLabelAndDescription labelToIconMapping={labelToIconMapping} label={label} description={description} />}
            </MuiLink>
    }
    else {
        const buttonVariant = getMuiButtonVariant(buttonType, "text");
        const buttonColorFromProps = getMuiButtonColor(buttonColor, "inherit");
        let buttonSx = { ...ButtonProps?.sx }
        if (buttonVariant === 'outlined' && buttonColor === 'White') {
            buttonSx = { ...buttonSx, border: '1px solid white' }
        }
        if (buttonVariant === 'contained' && buttonColor === 'White') {
            buttonSx = { ...buttonSx, backgroundColor: 'white', color: theme.palette.text.primary, border: 'none' }
        }
        if (buttonColor === 'PrimaryGradient') {
            buttonSx = { ...buttonSx, color: 'white', background: `var(--Liner, linear-gradient(135deg, #6ABD45 0%, #004E17 100%))`, border: 'none' }
        }
        switch (icon) {
            case 'Phone': {
                endIcon = <Phone />
                break;
            }
            case 'Right arrow': {
                endIcon = <ChevronRight />
                break;
            }
        }

        // console.log('heree but', { buttonSx, other, ButtonProps })

        return relativeLinkComponent && !isAbsoluteUrl ?
            <Button component={relativeLinkComponent} variant={buttonVariant} color={buttonColorFromProps} href={url || undefined} to={url || undefined} {...other} endIcon={endIcon || ButtonProps?.endIcon} onClick={onClick} sx={{ fontSize: 'inherit', ...other.sx, ...(buttonSx as any) }} {...ButtonProps}>
                <LinkLabelAndDescription labelToIconMapping={labelToIconMapping} label={label} description={description} />
            </Button>
            :
            <Button color={buttonColorFromProps} variant={buttonVariant} href={url || undefined} {...isAbsoluteUrl ? { target: '_blank', rel: 'noopener noreferrer' } : undefined} {...other} endIcon={endIcon || ButtonProps?.endIcon} onClick={onClick} sx={{ fontSize: 'inherit', ...other.sx, ...(buttonSx as any) }} {...ButtonProps}>
                {children ? children : <LinkLabelAndDescription labelToIconMapping={labelToIconMapping} label={label} description={description} />}
            </Button>
    }
}